import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { PrincipalService } from '@core/auth/principal.service';

interface HealthTab {
  label: string;
  route: string;
  id: string;
  visible: boolean;
  /** Tab is shown only if the user has any of these authorities. */
  requiredAuthorities?: string[];
  /** Tab is hidden if the user has any of these authorities. */
  hideFromAuthorities?: string[];
  /**
   * Renders the tab as a link out to a third-party tool: preceded by a divider
   * and outlined rather than filled.
   */
  setApart?: boolean;
}

/**
 * Static navigation bar shared across the Device / WAN / LAN / WiFi health
 * pages. It always renders the same set of tabs (subject to the user's
 * authorities) and highlights the active route, so the tab bar stays consistent
 * no matter which page you're on.
 */
@Component({
  selector: 'app-health-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, MatButtonModule],
  templateUrl: './health-nav.component.html',
  styleUrls: ['./health-nav.component.css']
})
export class HealthNavComponent implements OnInit {
  @Input() storeNumber: string = '';

  tabs: HealthTab[] = [
    {
      label: 'Device Health',
      route: '/health/device',
      id: 'nav-device-health-button',
      visible: true,
      requiredAuthorities: ['LOGIN', 'FEATURE_TOGGLE']
    },
    {
      label: 'WAN Health',
      route: '/health/wan',
      id: 'nav-wan-health-button',
      visible: true,
      hideFromAuthorities: ['HELP_1']
    },
    {
      label: 'LAN Health',
      route: '/health/lan',
      id: 'nav-lan-health-button',
      visible: true,
      requiredAuthorities: ['LOGIN', 'FEATURE_TOGGLE']
    },
    {
      label: 'WiFi Health',
      route: '/health/wifi',
      id: 'nav-wifi-health-button',
      visible: true,
      hideFromAuthorities: ['HELP_1']
    },
    {
      label: 'Datadog',
      route: '/health/datadog',
      id: 'nav-datadog-button',
      visible: true,
      requiredAuthorities: ['LOGIN', 'FEATURE_TOGGLE'],
      setApart: true
    }
  ];

  constructor(private principal: PrincipalService) {}

  ngOnInit(): void {
    this.updateVisibility();
    this.principal.getActiveUser().subscribe(() => this.updateVisibility());
  }

  private updateVisibility(): void {
    this.tabs.forEach(tab => {
      let visible = true;
      if (tab.requiredAuthorities) {
        visible = this.principal.hasAnyAuthority(tab.requiredAuthorities);
      }
      if (visible && tab.hideFromAuthorities) {
        visible = !this.principal.hasAnyAuthority(tab.hideFromAuthorities);
      }
      tab.visible = visible;
    });
  }
}
